import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from './Input';

const Container = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(p) => (p.$error ? p.theme.destructive : p.theme.foreground)};
`;

const Trigger = styled.button<{ $error: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  text-align: left;
  background: ${(p) => p.theme.input || p.theme.background};
  border: 1px solid ${(p) => (p.$error ? p.theme.destructive : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  color: ${(p) => p.theme.foreground};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${(p) => p.theme.ring || p.theme.primary};
  }
  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.ring || p.theme.primary};
    box-shadow: 0 0 0 2px ${(p) => (p.theme.ring || p.theme.primary) + '40'};
  }
  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.mutedForeground};
  }
`;

const Popover = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 4px;
  z-index: 10001;
  display: ${(p) => (p.$open ? 'block' : 'none')};
  background: ${(p) => p.theme.card};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  padding: 12px;
  min-width: 280px;
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const MonthNav = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(p) => p.theme.foreground};
  cursor: pointer;
  &:hover {
    background: ${(p) => p.theme.muted};
  }
`;

const MonthTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(p) => p.theme.foreground};
`;

const WeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 4px;
`;

const Weekday = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p) => p.theme.mutedForeground};
  text-align: center;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const Day = styled.button<{ $other?: boolean; $selected?: boolean; $today?: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  background: ${(p) =>
    p.$selected ? p.theme.primary : p.$today ? p.theme.primary + '20' : 'transparent'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$other ? p.theme.mutedForeground : p.theme.foreground};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${(p) => (p.$selected ? p.theme.primary : p.theme.muted)};
    color: ${(p) => (p.$selected ? '#fff' : p.theme.foreground)};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const TimeRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const TimeLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(p) => p.theme.mutedForeground};
  margin-bottom: 6px;
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$error ? p.theme.destructive : p.theme.mutedForeground)};
`;

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getDaysInMonth(year: number, month: number) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay();
  const days = last.getDate();
  const pad: null[] = Array(startPad).fill(null);
  const dayNums = Array.from({ length: days }, (_, i) => i + 1);
  return [...pad, ...dayNums];
}

export interface ScheduleDateTimePickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const ScheduleDateTimePicker: React.FC<ScheduleDateTimePickerProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Pick date & time',
  error = false,
  helperText,
  fullWidth = true,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => value || new Date());
  const [timeStr, setTimeStr] = useState<string>(() => {
    if (!value) return '09:00';
    const h = value.getHours();
    const m = value.getMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  const valueDate = value ? new Date(value) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (value) {
      setViewDate(new Date(value.getFullYear(), value.getMonth(), 1));
      setTimeStr(
        `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
      );
    }
  }, [value?.getTime()]);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const displayLabel = value
    ? `${value.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at ${timeStr}`
    : placeholder;

  const handleDayClick = (year: number, month: number, day: number) => {
    const [h, m] = timeStr.split(':').map(Number);
    const d = new Date(year, month, day, h || 0, m || 0);
    onChange(d);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setTimeStr(v);
    const [h, m] = v.split(':').map(Number);
    if (isNaN(h)) return;
    const base = valueDate || new Date();
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, isNaN(m) ? 0 : m, 0, 0);
    onChange(d);
  };

  const days = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  return (
    <Container ref={containerRef} $fullWidth={fullWidth}>
      {label && (
        <Label $error={error}>
          {label}
        </Label>
      )}
      <div style={{ position: 'relative' }}>
        <Trigger
          type="button"
          $error={error}
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          <Calendar size={18} />
          <span style={{ flex: 1 }}>{displayLabel}</span>
        </Trigger>
        <Popover $open={open}>
          <CalendarHeader>
            <MonthNav
              type="button"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={18} />
            </MonthNav>
            <MonthTitle>
              {MONTHS[month]} {year}
            </MonthTitle>
            <MonthNav
              type="button"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={18} />
            </MonthNav>
          </CalendarHeader>
          <WeekdayRow>
            {WEEKDAYS.map((w) => (
              <Weekday key={w}>{w}</Weekday>
            ))}
          </WeekdayRow>
          <DayGrid>
            {days.map((day, i) => {
              if (day === null) return <Day key={`pad-${i}`} disabled $other />;
              const d = new Date(year, month, day);
              d.setHours(0, 0, 0, 0);
              const isSelected =
                valueDate &&
                valueDate.getDate() === day &&
                valueDate.getMonth() === month &&
                valueDate.getFullYear() === year;
              const isToday = d.getTime() === today.getTime();
              return (
                <Day
                  key={day}
                  $today={isToday}
                  $selected={!!isSelected}
                  type="button"
                  onClick={() => handleDayClick(year, month, day)}
                >
                  {day}
                </Day>
              );
            })}
          </DayGrid>
          <TimeRow>
            <TimeLabel>Time</TimeLabel>
            <Input
              type="time"
              value={timeStr}
              onChange={handleTimeChange}
              size="sm"
              style={{ width: '100%' }}
            />
          </TimeRow>
        </Popover>
      </div>
      {helperText && <HelperText $error={error}>{helperText}</HelperText>}
    </Container>
  );
};

export default ScheduleDateTimePicker;
